import type { DrawingDocument, ResolvedDocument } from '../contract/types.js';
import { buildPdfToDrawing, invert, PT_TO_MM } from './matrix.js';

/**
 * Resolving physical scale.
 *
 * Scale is never guessed. It comes from one of exactly three places, and if
 * none of them can supply it the document stays at unknown scale, the JSON and
 * SVG are marked partial, and no DXF is written.
 *
 * `resolveScale` does not move any geometry. Entities stay in PDF points so the
 * overlay remains aligned to the original page; what changes is that the
 * document now *carries* the transform into drawing millimetres, and its type
 * now permits a DXF to be written from it.
 */
export type ScaleSource =
  /** Declared by a fixture manifest or by the caller. */
  | { kind: 'manifest'; mmPerPoint: number }
  /**
   * Manual calibration: an operator clicks an origin and drags across a known
   * distance. Production-proven in Line Forge, not on AIONA's board, raised as
   * open question Q23. It turns "this drawing cannot be processed" into thirty
   * seconds of human input.
   */
  | { kind: 'calibration'; pdfDistance: number; realMm: number }
  /**
   * Derived from the paper size and the drawing scale stated in the title block.
   *
   * Prefer `sheet`. This variant exists for the case the sheet cannot measure
   * itself — a page whose media box is not the real sheet size — and for the two
   * command-line tools and the fixture manifests, which state the paper
   * explicitly. Naming the wrong paper here is silent and produces a DXF at the
   * wrong scale, so it is not what a person should be asked for.
   */
  | { kind: 'page'; paper: 'A4' | 'A3'; drawingScale: number }
  /**
   * The sheet measures itself; the operator supplies only the drawing scale.
   *
   * A PDF point is 1/72 inch **by definition**, so a correctly produced page
   * already states its own physical width and nobody needs to re-state it. That
   * matters because the paper size was the one input a person could get wrong
   * without being told: an A3 drawing declared A4 yields a DXF at 71% of true
   * size, silently, which is exactly the failure the never-guess rule exists to
   * prevent. The scale printed in the title block is the one thing the file
   * cannot supply, so it is the only thing asked for.
   */
  | { kind: 'sheet'; drawingScale: number };

const PAPER_WIDTH_MM = { A4: 210, A3: 297 } as const;

/** ISO A sheet sizes in millimetres, portrait. Engineering drawings run to A0. */
const SHEET_SIZES_MM = {
  A0: [841, 1189],
  A1: [594, 841],
  A2: [420, 594],
  A3: [297, 420],
  A4: [210, 297],
} as const;

export type SheetName = keyof typeof SHEET_SIZES_MM;

export interface SheetMeasurement {
  /** The page's own width in millimetres. Measured from the media box, not chosen. */
  widthMm: number;
  heightMm: number;
  /** The ISO A size this page matches, or `null` when it matches none. */
  name: SheetName | null;
  orientation: 'portrait' | 'landscape';
}

/**
 * How big the sheet physically is, read off the page.
 *
 * Reported as `null` rather than snapped to the nearest size when nothing
 * matches: a page that is not a standard sheet is a thing the operator should be
 * told about, not a thing to round away.
 */
export function measureSheet(widthPt: number, heightPt: number): SheetMeasurement {
  const widthMm = widthPt * PT_TO_MM;
  const heightMm = heightPt * PT_TO_MM;
  const orientation = widthMm > heightMm ? 'landscape' : 'portrait';
  const shortSide = Math.min(widthMm, heightMm);
  const longSide = Math.max(widthMm, heightMm);

  // Producers round the media box, and 842 pt is 297.04 mm rather than 297.
  const TOLERANCE_MM = 2;
  let name: SheetName | null = null;
  for (const [candidate, [w, h]] of Object.entries(SHEET_SIZES_MM) as [SheetName, readonly [number, number]][]) {
    if (Math.abs(shortSide - w) <= TOLERANCE_MM && Math.abs(longSide - h) <= TOLERANCE_MM) {
      name = candidate;
      break;
    }
  }
  return { widthMm, heightMm, name, orientation };
}

export function resolveScale(
  doc: DrawingDocument<'unknown'>,
  source: ScaleSource,
): ResolvedDocument {
  const mmPerPoint = computeMmPerPoint(doc, source);
  if (!(mmPerPoint > 0) || !Number.isFinite(mmPerPoint)) {
    throw new Error(`scale must be a positive finite number of millimetres per point, got ${mmPerPoint}`);
  }

  const firstPage = doc.pages[0];
  if (!firstPage) throw new Error('cannot resolve scale for a document with no pages');

  // Each page is flipped about its own height. Building one transform from the
  // first page is correct only when every page is the same size, and wrong
  // silently otherwise.
  const pages = doc.pages.map((page) => {
    const transform = buildPdfToDrawing(page.heightPt, mmPerPoint);
    return { ...page, transform, inverse: invert(transform) };
  });

  const first = pages[0]!;

  return {
    ...doc,
    scaleState: 'resolved',
    unit: 'mm',
    mmPerPoint,
    pages,
    provenance: {
      ...doc.provenance,
      // Kept for the single-page case and for readers that expect one matrix;
      // the authority is the per-page transform.
      transform: first.transform,
      inverse: first.inverse,
      sourceSpace: 'pdf_pt',
      // Page y increases downward, so a rising angle turns clockwise on the sheet.
      angleSense: 'clockwise_on_page',
      targetSpace: 'drawing_mm',
    },
    qualityIssues: doc.qualityIssues.filter((q) => q.kind !== 'undetermined_scale'),
  };
}

/** The scale a 1:1 drawing has: one PDF point is 25.4/72 millimetres. */
export const ONE_TO_ONE = PT_TO_MM;

function computeMmPerPoint(doc: DrawingDocument<'unknown'>, s: ScaleSource): number {
  switch (s.kind) {
    case 'manifest':
      return s.mmPerPoint;
    case 'calibration': {
      if (!(s.pdfDistance > 0)) {
        throw new Error('calibration distance must be a positive number of points');
      }
      return s.realMm / s.pdfDistance;
    }
    case 'page': {
      const page = doc.pages[0];
      if (!page) throw new Error('cannot derive scale from a document with no pages');
      if (!(s.drawingScale > 0)) throw new Error('drawing scale must be positive');
      /*
        The sheet is `paper` wide in millimetres of paper; the drawing on it is at
        `drawingScale`, so a point of paper is that many millimetres of part.

        **Against the page's width in the PAPER's own orientation.** `page.widthPt`
        is pdf.js's `viewport.width`, and pdf.js swaps width and height for a page
        rotated 90 or 270 — so this divided the paper's WIDTH by the page's HEIGHT
        and, on A-series paper, was out by the aspect ratio: measured **-29.29 %**
        on fixture F02 (A4, rotation 90), 0.249439 against 0.352778, with the whole
        DXF uniformly mis-scaled and its sheet border reading 190.045 x 128.530 mm
        instead of 268.778 x 181.778.

        The acceptance runner never saw it: `scripts/acceptance.ts` computes its own
        mmPerPoint through a `basePageWidth(widthPt, heightPt, rotation)` that
        un-swaps the axis, so the harness knew about the rotation and the code
        under test did not.
      */
      const turned = page.rotation === 90 || page.rotation === 270;
      const acrossPaper = turned ? page.heightPt : page.widthPt;
      return (PAPER_WIDTH_MM[s.paper] / acrossPaper) * s.drawingScale;
    }
    case 'sheet': {
      if (!(s.drawingScale > 0)) throw new Error('drawing scale must be positive');
      // A point of paper is 25.4/72 mm of paper by definition, and the drawing on
      // it is reduced by `drawingScale`, so a point is that many millimetres of
      // part. No page measurement enters: this is what `page` computes when the
      // paper named matches the page, which is the only case it is right in.
      return PT_TO_MM * s.drawingScale;
    }
  }
}

/**
 * A scale ratio the drawing states about itself.
 *
 * Read from the extracted text so the operator can be shown what the title block
 * says instead of being asked for something printed in front of them. It is
 * offered, never applied: `resolveScale` still requires an explicit source, so a
 * misread title block cannot become a wrong-scale DXF without a person agreeing
 * to it. That is the whole distinction between a suggestion and a guess.
 */
export interface ScaleHint {
  /** The ratio as printed, normalised to `a:b`. */
  text: string;
  /** Real millimetres per millimetre of paper — what `drawingScale` takes. */
  drawingScale: number;
  /** Where it was read, so the operator can check it against the sheet. */
  pageIndex: number;
  entityId: string;
  /** The whole run it came from, e.g. `SCALE 1:1`. */
  sourceText: string;
}

/** SCALE, 尺度 and the abbreviations that appear on Japanese and English sheets. */
const SCALE_KEYWORD = /(?:scale|scl|尺度|縮尺)\s*[:=]?|(?:^|\s)s\s*[:=]/i;
/** A ratio, written with a colon or a solidus. */
const RATIO = /(\d{1,4})\s*[:/]\s*(\d{1,4})/g;

export function readScaleHints(doc: DrawingDocument): ScaleHint[] {
  const found = new Map<string, ScaleHint>();

  for (const page of doc.pages) {
    for (const e of page.entities) {
      if (e.type !== 'TEXT' && e.type !== 'MTEXT') continue;
      const text = normaliseFullWidth(e.text);
      const keyed = SCALE_KEYWORD.test(text);
      // A bare `1:1` in its own cell is a scale; a bare `1:1` inside a sentence
      // is not, and `JIS B 0405` must never read as one.
      if (!keyed && !/^\s*\d{1,4}\s*[:/]\s*\d{1,4}\s*$/.test(text)) continue;

      RATIO.lastIndex = 0;
      for (let m = RATIO.exec(text); m !== null; m = RATIO.exec(text)) {
        const a = Number(m[1]);
        const b = Number(m[2]);
        if (!(a > 0) || !(b > 0)) continue;
        const key = `${a}:${b}`;
        if (found.has(key)) continue;
        found.set(key, {
          text: key,
          drawingScale: b / a,
          pageIndex: page.index,
          entityId: e.id,
          sourceText: e.text,
        });
      }
    }
  }

  // Insertion order is document order, which is the order a reader would meet
  // them; a title block states the sheet's scale before any detail overrides it.
  return [...found.values()];
}

/** `１：２` is the same scale as `1:2`, and Japanese title blocks use both. */
function normaliseFullWidth(text: string): string {
  return text.replace(/[！-～]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
  );
}
